import { Box, Grid, Tooltip, Typography } from '@mui/material'
import PercentileChartElementBar from 'components/PercentileChartElement/Bar'
import PercentileChartElementCohort, {
  PercentileCohortData,
} from 'components/PercentileChartElement/Cohort'
import { Maybe } from 'graphql/jsutils/Maybe'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import useReportDetails from 'hooks/useReportDetails'
import React from 'react'
import getCohortData from 'util/getCohortData'
import getPercentileLabel from 'util/getPercentilLabel'
import useKeyMeasureRow from 'hooks/useKeyMeasureRow'
import KeyRatingsChartElementPrintRow from './PrintRow'

export type KeyRatingsChartElementRowProps = {
  reportId: string
  data_source_name: string
  description: string
  key_measures_chart_id: number
  order: number
  prefix: string
  suffix: string
  title: string
  cohort: Maybe<string> | undefined
  subgroup: Maybe<string> | undefined
  subgroupItem: Maybe<string> | undefined
  data: any
  everStatic: boolean
  print?: boolean
}

const KeyRatingsChartElementRow: React.FC<KeyRatingsChartElementRowProps> = ({
  reportId,
  title,
  description,
  prefix,
  suffix,
  cohort,
  subgroup,
  subgroupItem,
  data,
  everStatic,
  print = false,
}) => {
  const { parsedData } = useReportDetails(reportId)
  let cohortData = getCohortData({
    cohortName: cohort || undefined,
    cohorts: data?.cohorts || [],
    cohortSynonyms: parsedData.cohortSynonyms,
    chartType: 'KeyRatingsRow',
  }) as PercentileCohortData | undefined
  if (data.type === 'toggledBarChart') {
    cohortData = undefined
  }

  const { subgroupItemData, averageData, chartPercentage } = useKeyMeasureRow(
    data,
    subgroup,
    subgroupItem,
    prefix,
    suffix,
    everStatic,
    'sort_order'
  )

  const lineData = data.static_current?.name
    ? data.static_trend_data
    : data.trend_data

  const lineChartOptions = {
    title: null,
    exporting: {
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    chart: {
      height: '100px',
      marginBottom: 0,
      marginTop: 0,
      type: 'area',
      marginLeft: 5,
      marginRight: 5,
      animation: !print,
    },
    legend: {
      enabled: false,
    },
    yAxis: {
      labels: {
        enabled: false,
      },
      title: null,
      max: 100,
      min: 0,
      tickInterval: 50,
      girdLineWidth: 1,
      gridLineColor: '#BDBDBD',
      gridLineDashStyle: 'shortDash',
    },
    xAxis: {
      labels: {
        enabled: false,
      },
      title: null,
      lineColor: 'transparent',
      tickLength: 0,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      formatter: function (
        this: Highcharts.TooltipFormatterContextObject
      ): string {
        const percentileLabel = this.y
          ? getPercentileLabel(Math.round(this.y))
          : ''
        return `<strong>${percentileLabel}</strong>`
      },
    },
    series: [
      {
        name: '',
        data: lineData,
        color: '#757575',
        fillColor: 'rgba(117, 117, 117, 0.1)',
      },
    ],
  }

  let colorPalette = data.color_palette
  if (everStatic) {
    colorPalette = 'static'
  } else {
    if (colorPalette === 'primary') {
      colorPalette = subgroupItemData ? 'secondary' : 'primary'
    } else {
      colorPalette = subgroupItemData ? 'secondaryGreen' : 'primaryTan'
    }
  }

  let chartValue = subgroupItemData
    ? subgroupItemData.value
    : data.current.value
  if (data.static_current.name) {
    chartValue = subgroupItemData
      ? subgroupItemData.value
      : data.static_current.value
  }
  // does static past result apply to toggledBarChart ????
  const pastCol = data.static_current?.name
    ? 'static_past_results'
    : 'past_results'
  const showTrend = (!subgroup || !subgroupItem) && data.has_past_data
  const showTrendNa = showTrend && (data[pastCol] || []).length === 0
  const showTrendCol = showTrend || showTrendNa

  if (data) {
    if (everStatic && chartPercentage === undefined) {
      return null
    }
    if (print) {
      return (
        <KeyRatingsChartElementPrintRow
          title={title}
          description={description}
          chartPercentage={chartPercentage}
          averageData={averageData}
          chartValue={chartValue}
          colorPalette={colorPalette}
          data={data}
          cohortData={cohortData}
          showTrendCol={showTrendCol}
          showTrendNa={showTrendNa}
          lineChartOptions={lineChartOptions}
        />
      )
    }
    return (
      <>
        <Box
          sx={{
            border: '1px solid #E0E0E0',
            borderTopColor: 'transparent',
            padding: '20px 30px',
            position: 'relative',
            zIndex: '0',
            pageBreakInside: 'avoid',
            // width: print ? (showTrendCol ? '1200px' : '1000px') : 'auto',
          }}
        >
          <Grid
            container
            spacing={8}
            alignItems="center"
            sx={{ maxWidth: print ? '1200px' : null }}
          >
            <Grid item sm={print ? 3 : 12} md={3}>
              <Typography variant="h5">{title}</Typography>
              {description && (
                <Typography component="p" sx={{ fontSize: '0.8rem' }}>
                  {description}
                </Typography>
              )}
            </Grid>
            <Grid item sm={print ? 2 : 12} md={2}>
              {chartPercentage !== null && chartPercentage !== undefined && (
                <Typography align="center" component="div" variant="h4">
                  {averageData}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              sm={print ? (showTrendCol ? 4 : 7) : 12}
              md={showTrendCol ? 4 : 7}
            >
              <Box sx={{ width: '100%' }}>
                {chartPercentage !== null && chartPercentage !== undefined ? (
                  <PercentileChartElementBar
                    percentage={chartPercentage}
                    showBoldPercentile
                    label=""
                    value={chartValue}
                    prefix={data.prefix}
                    suffix={data.suffix}
                    significantDigits={data.absolutes}
                    size={40}
                    color={colorPalette}
                  />
                ) : (
                  <Typography align="center">N/A</Typography>
                )}
              </Box>
              {cohortData && (
                <Box>
                  {chartPercentage !== null &&
                    chartPercentage !== undefined && (
                      <>
                        <PercentileChartElementCohort
                          data={
                            cohortData || {
                              max: 0,
                              median: 0,
                              min: 0,
                              name: '',
                            }
                          }
                          size={20}
                          hideLabel
                        />
                        <Typography component="small" sx={{ fontSize: '12px' }}>
                          {cohortData.name}
                        </Typography>
                      </>
                    )}
                </Box>
              )}
            </Grid>
            {showTrendCol && (
              <Grid item sm={print ? 3 : 12} md={3}>
                <Box
                  sx={{
                    borderTop: !showTrendNa ? '1px solid #BDBDBD' : '',
                    borderBottom: !showTrendNa ? '1px solid #BDBDBD' : '',
                  }}
                >
                  {showTrendNa ? (
                    <Typography align="center">N/A</Typography>
                  ) : (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={lineChartOptions}
                    ></HighchartsReact>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </>
    )
  }
  return null
}

export default KeyRatingsChartElementRow
